/**
 * BookingDetail.jsx
 * Shows booking details, contract signing status, and guest key status.
 * Route: /booking/:id
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-600',
  canceled:  'bg-red-100 text-red-700',
};

const CONTRACT_COLORS = {
  not_required: 'bg-gray-100 text-gray-500',
  pending:      'bg-yellow-100 text-yellow-700',
  sent:         'bg-blue-100 text-blue-700',
  viewed:       'bg-blue-100 text-blue-700',
  renter_signed:'bg-yellow-100 text-yellow-700',
  signed:       'bg-green-100 text-green-700',
  expired:      'bg-red-100 text-red-700',
};

const GUEST_KEY_COLORS = {
  not_applicable:      'bg-gray-100 text-gray-500',
  // Legacy statuses
  pending:             'bg-yellow-100 text-yellow-700',
  active:              'bg-green-100 text-green-700',
  revoked:             'bg-gray-100 text-gray-500',
  // New Guest Mode statuses
  page_ready:          'bg-blue-100 text-blue-700',
  guest_mode_active:   'bg-green-100 text-green-700',
  guest_mode_disabled: 'bg-gray-100 text-gray-500',
  failed:              'bg-red-100 text-red-700',
};

const GUEST_KEY_LABELS = {
  not_applicable:      'Not Applicable',
  pending:             'Scheduled',
  active:              'Active',
  revoked:             'Revoked',
  page_ready:          'Portal Ready',
  guest_mode_active:   'Guest Mode Active ✓',
  guest_mode_disabled: 'Access Ended',
  failed:              'Failed',
};

function formatCents(c) { return `$${(c / 100).toFixed(2)}`; }

export default function BookingDetail() {
  const { id } = useParams();
  const api    = useApi();

  const [booking, setBooking]         = useState(null);
  const [vehicle, setVehicle]         = useState(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const [showChangeForm, setShowChangeForm] = useState(false);
  const [newStart, setNewStart]             = useState('');
  const [newEnd, setNewEnd]                 = useState('');
  const [changeReason, setChangeReason]     = useState('');
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [changeError, setChangeError]       = useState('');

  const [canceling, setCanceling]     = useState(false);
  const [cancelError, setCancelError] = useState('');

  const loadBooking = () => {
    return Promise.all([
      api.get(`/bookings/${id}`),
      api.get(`/bookings/${id}/inspection`),
    ])
      .then(([bRes, iRes]) => {
        setBooking(bRes.data);
        setInspections(iRes.data || []);
        if (bRes.data?.vin) {
          api.get(`/vehicles/${bRes.data.vin}`).then(r => setVehicle(r.data)).catch(() => {});
        }
      })
      .catch(() => setError('Failed to load booking'));
  };

  useEffect(() => {
    loadBooking().finally(() => setLoading(false));
  }, [id]);

  const submitChangeRequest = async (e) => {
    e.preventDefault();
    setChangeError('');
    if (!newStart || !newEnd) {
      setChangeError('Please select both a new start and end date/time.');
      return;
    }
    setChangeSubmitting(true);
    try {
      await api.post(`/bookings/${id}/request-change`, {
        new_start_time: new Date(newStart).toISOString(),
        new_end_time:   new Date(newEnd).toISOString(),
        reason:         changeReason,
      });
      setShowChangeForm(false);
      setNewStart(''); setNewEnd(''); setChangeReason('');
      await loadBooking();
    } catch (err) {
      setChangeError(err.response?.data?.error || 'Failed to submit change request');
    } finally {
      setChangeSubmitting(false);
    }
  };

  const cancelChangeRequest = async () => {
    setChangeSubmitting(true);
    setChangeError('');
    try {
      await api.delete(`/bookings/${id}/request-change`);
      await loadBooking();
    } catch (err) {
      setChangeError(err.response?.data?.error || 'Failed to cancel change request');
    } finally {
      setChangeSubmitting(false);
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm('Cancel this booking? Any completed payment will be refunded and the security deposit hold will be released.')) return;
    setCanceling(true);
    setCancelError('');
    try {
      await api.delete(`/bookings/${id}`);
      await loadBooking();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setCanceling(false);
    }
  };


  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (error)   return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (!booking) return null;

  const preTrip  = inspections.find(i => i.type === 'pre_trip');
  const postTrip = inspections.find(i => i.type === 'post_trip');
  const tripStarted = new Date(booking.startTime).getTime() <= Date.now();
  const tripEnded    = new Date(booking.endTime).getTime() <= Date.now();
  const canCancel = ['pending', 'confirmed'].includes(booking.status) && !tripStarted;
  const canRequestChange = ['confirmed', 'active'].includes(booking.status) && !tripEnded;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
            <p className="text-gray-400 text-sm font-mono mt-1">{id}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[booking.status] || 'bg-gray-100 text-gray-600'}`}>
            {booking.status}
          </span>
        </div>

        {/* Main info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Rental Info</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-gray-400">Vehicle</dt><dd className="font-medium">{vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : booking.vin}</dd></div>
            <div><dt className="text-gray-400">Source</dt><dd className="font-medium capitalize">{booking.source}</dd></div>
            <div><dt className="text-gray-400">Pickup</dt><dd className="font-medium">{new Date(booking.startTime).toLocaleString()}</dd></div>
            <div><dt className="text-gray-400">Return</dt><dd className="font-medium">{new Date(booking.endTime).toLocaleString()}</dd></div>
            <div><dt className="text-gray-400">Total Paid</dt><dd className="font-medium">{formatCents(booking.totalAmountCents || 0)}</dd></div>
            <div><dt className="text-gray-400">Deposit Hold</dt><dd className="font-medium">{formatCents(booking.depositAmountCents || 0)}</dd></div>
            {booking.loyaltyPointsEarned > 0 && (
              <div><dt className="text-gray-400">Points Earned</dt><dd className="font-medium text-blue-600">+{booking.loyaltyPointsEarned} pts</dd></div>
            )}
          </dl>

          {canCancel && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              {cancelError && <p className="text-sm text-red-600 mb-2">{cancelError}</p>}
              <button
                onClick={cancelBooking}
                disabled={canceling}
                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                {canceling ? 'Canceling…' : 'Cancel Booking'}
              </button>
              <p className="text-xs text-gray-400 mt-1">Any completed payment will be refunded and the deposit hold released.</p>
            </div>
          )}
          {['pending', 'confirmed'].includes(booking.status) && tripStarted && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                This trip has already {tripEnded ? 'ended' : 'started'} and can no longer be canceled online.
                Contact us at <a href="mailto:hello@drivedobias.com" className="text-blue-600 hover:underline">hello@drivedobias.com</a> if you need help.
              </p>
            </div>
          )}
        </div>

        {/* Request Date Change */}
        {['confirmed', 'active'].includes(booking.status) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Trip Dates</h2>
              {booking.pendingChangeRequest && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  Change Pending Review
                </span>
              )}
            </div>

            {!canRequestChange && !booking.pendingChangeRequest ? (
              <p className="text-sm text-gray-500">
                This trip has already ended and its dates can no longer be changed.
                Contact us at <a href="mailto:hello@drivedobias.com" className="text-blue-600 hover:underline">hello@drivedobias.com</a> if you need help.
              </p>
            ) : booking.pendingChangeRequest ? (
              <div className="space-y-3">
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-gray-400">Requested Pickup</dt><dd className="font-medium">{new Date(booking.pendingChangeRequest.newStartTime).toLocaleString()}</dd></div>
                  <div><dt className="text-gray-400">Requested Return</dt><dd className="font-medium">{new Date(booking.pendingChangeRequest.newEndTime).toLocaleString()}</dd></div>
                </dl>
                {booking.pendingChangeRequest.reason && (
                  <p className="text-sm text-gray-600">Reason: {booking.pendingChangeRequest.reason}</p>
                )}
                <p className="text-sm text-gray-500">Your request is awaiting admin review. You can cancel it below.</p>
                {changeError && <p className="text-sm text-red-600">{changeError}</p>}
                <button
                  onClick={cancelChangeRequest}
                  disabled={changeSubmitting}
                  className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  {changeSubmitting ? 'Canceling…' : 'Cancel Change Request'}
                </button>
              </div>
            ) : showChangeForm ? (
              <form onSubmit={submitChangeRequest} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">New Pickup</label>
                    <input
                      type="datetime-local"
                      value={newStart}
                      onChange={e => setNewStart(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">New Return</label>
                    <input
                      type="datetime-local"
                      value={newEnd}
                      onChange={e => setNewEnd(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={changeReason}
                    onChange={e => setChangeReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Flight delayed a day"
                  />
                </div>
                {changeError && <p className="text-sm text-red-600">{changeError}</p>}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={changeSubmitting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {changeSubmitting ? 'Submitting…' : 'Submit Request'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowChangeForm(false); setChangeError(''); }}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowChangeForm(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Request Date Change →
              </button>
            )}
          </div>
        )}

        {/* Contract status */}
        {booking.contractStatus !== 'not_required' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Rental Agreement</h2>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${CONTRACT_COLORS[booking.contractStatus] || ''}`}>
                {booking.contractStatus}
              </span>
            </div>
            {booking.contractStatus === 'pending' && (
              <p className="text-sm text-gray-600">
                Your rental agreement is being prepared and will be ready to sign shortly.
              </p>
            )}
            {(booking.contractStatus === 'sent' || booking.contractStatus === 'viewed') && (
              booking.contractSigningUrl ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="mb-3 text-sm text-blue-900">
                    SignWell opens the secure agreement in a separate tab.
                  </p>
                  <a
                    href={booking.contractSigningUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Open & Sign Agreement ↗
                  </a>
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Your rental agreement has been sent to your email. Please sign it to complete your booking.
                </p>
              )
            )}
            {booking.contractStatus === 'renter_signed' && (
              <p className="text-sm text-amber-700">
                Your signature is complete. Drive Dobias has been notified to countersign the agreement.
              </p>
            )}
            {booking.contractStatus === 'signed' && (
              <p className="text-sm text-green-700">✓ Signed on {new Date(booking.contractSignedAt).toLocaleDateString()}</p>
            )}
          </div>
        )}

        {/* Guest key status (Tesla only) */}
        {booking.guestKeyStatus !== 'not_applicable' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Vehicle Access</h2>
              {booking.guestKeyStatus === 'page_ready' && (booking.guestAccessUrl || booking.guestKeyLink) ? (
                <a
                  href={booking.guestAccessUrl || booking.guestKeyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-2 py-1 rounded-full text-xs font-medium underline decoration-dotted hover:opacity-80 transition-opacity ${GUEST_KEY_COLORS['page_ready']}`}
                >
                  {GUEST_KEY_LABELS['page_ready']} →
                </a>
              ) : (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${GUEST_KEY_COLORS[booking.guestKeyStatus] || ''}`}>
                  {GUEST_KEY_LABELS[booking.guestKeyStatus] || booking.guestKeyStatus}
                </span>
              )}
            </div>
            {booking.guestKeyStatus === 'active' && booking.guestKeyLink && (
              <div>
                <p className="text-sm text-gray-600 mb-3">Your vehicle access link is active:</p>
                <a
                  href={booking.guestKeyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                >
                  Open Vehicle Access →
                </a>
              </div>
            )}
            {booking.guestKeyStatus === 'pending' && (
              <p className="text-sm text-gray-500">
                Vehicle access will be sent to your email 30 minutes before pickup.
              </p>
            )}
          </div>
        )}

        {/* Inspection reports */}
        {(preTrip || postTrip) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Inspection Reports</h2>
            <div className="space-y-4">
              {[preTrip, postTrip].filter(Boolean).map(insp => (
                <div key={insp.type} className="border border-gray-100 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 capitalize mb-2">
                    {insp.type === 'pre_trip' ? '🔍 Pre-Trip' : '✅ Post-Trip'} Inspection
                  </h3>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <div><dt className="text-gray-400">Mileage</dt><dd>{insp.mileage?.toLocaleString()} mi</dd></div>
                    <div><dt className="text-gray-400">Battery/Fuel</dt><dd>{insp.fuelOrBatteryPct}%</dd></div>
                    <div><dt className="text-gray-400">Exterior</dt><dd className="capitalize">{insp.exteriorCondition?.replace('_', ' ')}</dd></div>
                    <div><dt className="text-gray-400">Interior</dt><dd className="capitalize">{insp.interiorCondition?.replace('_', ' ')}</dd></div>
                  </dl>
                  {insp.damageNotes && (
                    <p className="mt-2 text-sm text-red-600">⚠ {insp.damageNotes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to="/profile?tab=my-trips" className="block text-center text-sm text-blue-600 hover:underline">
          ← Back to My Bookings
        </Link>
      </div>
    </div>
  );
}
